import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import clearNotNumber from '@vr/devkit/utils/clearNotNumber';
import { ToastCtx } from '~/providers/ToastProvider';
import LoginForm from '../components/forms/LoginForm';
import { Logo } from '../elements';
import { users } from '../helpers/api';
import Loader from '../components/Loader';
import { Container, PageContainer } from './common';
import messages from '../helpers/api/messages';
import { loadUserRequest } from '~/store/modules/user/actions';
import { loginRequest } from '~/store/modules/login/actions';
import { toggleLoading } from '~/store/modules/loading/actions';
import isIOS from '~/helpers/isIOS';
import {
  recaptchaRequest,
  setSuccessKey,
} from '~/store/modules/recaptcha/actions';

const LoginContainer = styled(PageContainer)`
  position: relative;
  flex-direction: column;
  align-items: center;
  padding-bottom: 15px;
  margin-top: 30px;

  @media (min-width: ${({ theme }) => theme.devices.desktop}) {
    margin-top: 20px;
    padding-bottom: 35px;
    box-sizing: border-box;
    height: 100vh;
    justify-content: center;
  }
`;
const FormContainer = styled(Container)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 60px auto 0;
  max-width: 335px;
`;
const SendConfirmation = styled.span`
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
`;

const Link = styled.a`
  font-weight: 700;
  color: white;
`;

export default function Login() {
  const dispatch = useDispatch();
  const toast = useContext(ToastCtx);
  const isLoading = useSelector(state => state.loading);
  const issuer = useSelector(state => state?.issuer);
  const [resetFormCaptcha, setResetFormCaptcha] = useState(false);
  const { siteKey } = useSelector(state => state.recaptcha);
  const location = useLocation();
  const [route, setRoute] = useState({
    to: '',
    from: location?.pathname,
  });

  async function getReCaptchaKey() {
    try {
      dispatch(recaptchaRequest());
      dispatch(toggleLoading(true));
    } catch (e) {
      toast.dispatch(messages(e, 'recaptcha'));
    } finally {
      dispatch(toggleLoading(false));
    }
  }

  useEffect(() => {
    if (!siteKey) {
      getReCaptchaKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Limpando a chave de sucesso na alteração de rota para evitar conflito com outras telas que usam do mesmo local na store
   * O usuário poderia entrar na tela, clicar no recaptcha, e depois ir pra outra tela com recapctha e já estar validado, pois a store é global
   * Vêr a possibilidade de converter para um custom hook no futuro
   * */
  useEffect(() => {
    if (route?.to !== route?.from) {
      setRoute(prev => ({ to: location?.pathname, from: prev?.to }));

      dispatch(setSuccessKey(''));
    }
  }, [location]);

  async function handleEmailConfirmation(email) {
    try {
      dispatch(toggleLoading(true));
      const response = await users.resendEmailAuthentication(email);
      toast.dispatch(
        messages(
          response,
          'confirm_email',
          <>
            Um link de confirmação foi enviado para o e-mail: {email}. Caso não
            tenha recebido o e-mail de confirmação, verifique a sua caixa de
            spam ou lixeira.
          </>,
        ),
      );
    } catch (e) {
      toast.dispatch(messages(e, 'login'));
    } finally {
      dispatch(toggleLoading(false));
    }
  }

  function msgEmailConfirmation(email) {
    return (
      <>
        Para efetuar o login é necessário confirmar o seu cadastro no link
        enviado para o seu e-mail. Caso não tenha recebido, verifique sua caixa
        de Spam ou Lixeira, ou clique em &nbsp;
        <SendConfirmation
          aria-label="Enviar email de confirmação"
          onClick={() => handleEmailConfirmation(email)}
        >
          Enviar Novamente
        </SendConfirmation>
      </>
    );
  }

  async function handleUnlockLogin(email) {
    try {
      dispatch(toggleLoading(true));

      const response = await users.sendEmailUnlockUser(email);

      toast.dispatch(
        messages(
          response,
          'login',
          <>
            Enviamos um link para o e-mail {email} para redefinir sua senha e
            desbloquear seu login. Caso não tenha recebido, verifique a sua
            caixa de <strong>spam</strong> ou a <strong>lixeira</strong>.
          </>,
        ),
      );
    } catch (e) {
      toast.dispatch(messages(e, 'login', msgEmailConfirmation(email)));
    } finally {
      dispatch(toggleLoading(false));
    }
  }

  function msgUnlockLogin(payload) {
    return (
      <>
        Para sua segurança, seu login foi bloqueado por excesso de tentativas de
        acesso.&nbsp;
        <SendConfirmation
          aria-label="Clique aqui"
          onClick={() => handleUnlockLogin(payload?.email)}
        >
          <strong>Clique aqui</strong>
        </SendConfirmation>
        &nbsp;para realizar o desbloqueio.
      </>
    );
  }

  function handleLoginError(payload, error) {
    const errorMessages = error?.messages;
    let message;
    if (
      errorMessages?.find(item => item.code === '-20' || item.code === '-12')
    ) {
      message = msgUnlockLogin(payload);
    }
    if (errorMessages?.find(item => item.code === '-15')) {
      message = msgEmailConfirmation(payload?.email);
    }

    return message;
  }

  function handleUserProfile(payload) {
    dispatch(
      loadUserRequest(
        () => null,
        error => {
          toast.dispatch(
            messages(error, 'login', handleLoginError(payload, error)),
          );
        },
        () => dispatch(toggleLoading(false)),
        true,
      ),
    );
  }

  function getLink() {
    return isIOS() ? issuer?.app.ios : issuer?.app.android;
  }

  function renderWhatsapp() {
    const whatsapp = issuer?.phones?.find(item => item.type === 'whatsapp');

    const href = `tel:+55${clearNotNumber(whatsapp?.value)}`;
    const number = whatsapp?.value;

    return <Link href={href}>{number}</Link>;
  }

  function msgErrorLogin() {
    return (
      <>
        Por favor, tente novamente mais tarde ou acesse nossos outros canais de
        auto-atendimento:{' '}
        <Link href={getLink()} target="_blank">
          App {issuer?.app.name}
        </Link>
        , telefone ou WhastApp {renderWhatsapp()}
      </>
    );
  }

  /** After success, it's being redirect according to rules inside Routes.jsx component
   * Check the line using <Redirect /> from react-router-dom library
   */
  function handleSubmit(payload) {
    setResetFormCaptcha(false);
    dispatch(toggleLoading(true));
    dispatch(
      loginRequest(
        payload,
        () => {
          handleUserProfile(payload);
        },
        error => {
          setResetFormCaptcha(true);
          dispatch(toggleLoading(false));

          if (error?.messages === 'invalid_grant') {
            toast.dispatch(
              '',
              'error',
              'Verifique se o email e/ou senha digitados estão corretos',
            );
          } else if (error?.messages === 'unknown_error') {
            toast.dispatch(
              'Desculpe, estamos enfrentando um problema para efetuar o seu login',
              'error',
              msgErrorLogin(),
            );
          } else {
            toast.dispatch(messages(error, 'login'));
          }
        },
      ),
    );
  }

  return (
    <>
      <Loader isLoading={isLoading}>
        <LoginContainer>
          <Logo alt={issuer?.logo?.alt} src={issuer?.logo?.src} />
          <FormContainer>
            <LoginForm
              onSubmit={handleSubmit}
              resetCaptcha={resetFormCaptcha}
            />
          </FormContainer>
        </LoginContainer>
      </Loader>
    </>
  );
}
